"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useRouter } from "@/i18n/navigation";
import { useCart } from "@/context/cart-context";
import { useCustomerAuth } from "@/context/auth-context";
import { useNavTree } from "@/context/nav-tree-context";
import { useTranslations } from "next-intl";

const ease = [0.16, 1, 0.3, 1] as const;

/* ═══════════════ ICONS ═══════════════ */

function HomeIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 10.5L12 3l9 7.5V21a1 1 0 01-1 1H4a1 1 0 01-1-1V10.5z" />
    </svg>
  );
}

function ShopIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" />
      <rect x="14" y="3" width="7" height="7" />
      <rect x="3" y="14" width="7" height="7" />
      <rect x="14" y="14" width="7" height="7" />
    </svg>
  );
}

function MoreIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function BagIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 6h12l1 14H5z" />
      <path d="M9 6V4a3 3 0 016 0v2" />
    </svg>
  );
}

function UserIcon({ active }: { active: boolean }) {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill={active ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <circle cx="12" cy="8" r="4" />
      <path d="M5 21c0-4 3.5-7 7-7s7 3 7 7" />
    </svg>
  );
}

/* ═══════════════ DATA ═══════════════ */

/** Build URL-encoded collection href from path-like slug. */
function collectionHref(slug: string): string {
  return `/collecties/${slug.split("/").map(encodeURIComponent).join("/")}`;
}

/* ═══════════════ SLIDE-UP PANELS ═══════════════ */

function PanelBackdrop({ onClose }: { onClose: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 bg-foreground/30 z-[80]"
      onClick={onClose}
    />
  );
}

function PanelSheet({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ y: "100%" }}
      animate={{ y: 0 }}
      exit={{ y: "100%" }}
      transition={{ duration: 0.35, ease }}
      className="fixed bottom-14 left-0 right-0 z-[85] bg-background border-t border-border safe-bottom max-h-[70vh] overflow-y-auto"
      style={{ scrollbarWidth: "none" }}
    >
      {children}
    </motion.div>
  );
}

function ShopPanel({ open, onClose, t }: { open: boolean; onClose: () => void; t: ReturnType<typeof useTranslations> }) {
  const tree = useNavTree();
  const tMobile = useTranslations("mobileTab");
  const tNav = useTranslations("nav");
  const NAV_KEYS = ["gezicht", "ogen", "lippen", "accessoires", "informatie"] as const;
  type NavKey = (typeof NAV_KEYS)[number];
  const isKnown = (slug: string): slug is NavKey =>
    (NAV_KEYS as readonly string[]).includes(slug);
  return (
    <AnimatePresence>
      {open && (
        <>
          <PanelBackdrop onClose={onClose} />
          <PanelSheet>
            <div className="px-5 pt-5 pb-3">
              <Link
                href="/collecties/alle"
                onClick={onClose}
                className="flex items-center justify-between h-12 px-4 bg-foreground text-background rounded-xl"
              >
                <span className="text-sm font-medium tracking-wide">
                  {t("shopAlles")}
                </span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
              </Link>
              <Link
                href="/sets"
                onClick={onClose}
                className="flex items-center justify-between h-11 px-4 mt-2 border border-border rounded-xl"
              >
                <span className="text-sm font-medium tracking-wide text-foreground">
                  {t("sets")}
                </span>
                <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </Link>
            </div>
            <div className="px-5 pb-5">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-tertiary mb-3">
                {tMobile("categories")}
              </p>
              <div className="space-y-1">
                {tree
                  // `informatie` is the info hub, not a shopping
                  // category — skip so the panel doesn't link to the
                  // empty /collecties/informatie page. The MorePanel
                  // already exposes the info hub.
                  .filter((cat) => cat.slug !== "informatie")
                  .map((cat) => (
                  <Link
                    key={cat.id}
                    href={collectionHref(cat.slug)}
                    record={{ slug: cat.slug, slugs: cat.slugs ?? undefined }}
                    onClick={onClose}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <div>
                      <span className="text-sm font-medium text-foreground">
                        {isKnown(cat.slug) ? tNav(cat.slug) : cat.title}
                      </span>
                      {cat.productCount > 0 && (
                        <span className="text-xs text-text-tertiary ml-2">{cat.productCount}</span>
                      )}
                    </div>
                    <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          </PanelSheet>
        </>
      )}
    </AnimatePresence>
  );
}

function MorePanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslations("footer");
  const tNav = useTranslations("nav");

  const infoLinks = [
    { label: t("overOns"), href: "/over-ons" },
    // Mirror the desktop utility nav: this is the Informatie hub, not
    // an "ingredients" page. Using the nav label keeps the wording
    // consistent across desktop + mobile.
    { label: tNav("informatie"), href: "/informatie" },
    { label: t("contact"), href: "/contact" },
    { label: t("faq"), href: "/faq" },
  ];

  const serviceLinks = [
    { label: t("verzending"), href: "/verzending" },
    { label: t("garantie"), href: "/garantie" },
    { label: t("voorwaarden"), href: "/voorwaarden" },
    { label: t("privacy"), href: "/privacy" },
  ];

  return (
    <AnimatePresence>
      {open && (
        <>
          <PanelBackdrop onClose={onClose} />
          <PanelSheet>
            {/* Info section */}
            <div className="px-5 pt-5 pb-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-tertiary mb-3">
                {t("info")}
              </p>
              <div className="space-y-1">
                {infoLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={onClose}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <span className="text-sm font-medium text-foreground">{link.label}</span>
                    <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>

            {/* Service section */}
            <div className="px-5 pb-4 border-t border-border pt-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-tertiary mb-3">
                {t("service")}
              </p>
              <div className="space-y-1">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={onClose}
                    className="flex items-center justify-between py-3 border-b border-border last:border-0"
                  >
                    <span className="text-sm font-medium text-foreground">{link.label}</span>
                    <svg className="w-4 h-4 text-text-tertiary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M9 6l6 6-6 6" />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>

          </PanelSheet>
        </>
      )}
    </AnimatePresence>
  );
}

/* ═══════════════ MAIN COMPONENT ═══════════════ */

export function MobileTabBar() {
  const [active, setActive] = useState(0);
  const [shopOpen, setShopOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const { itemCount, openDrawer } = useCart();
  const { isAuthenticated } = useCustomerAuth();
  const router = useRouter();
  const t = useTranslations("nav");

  const closeAll = () => {
    setShopOpen(false);
    setMoreOpen(false);
  };

  const tabs = [
    { label: t("home"), Icon: HomeIcon, href: "/", id: "home" },
    { label: t("shop"), Icon: ShopIcon, href: undefined, id: "shop" },
    { label: t("meer"), Icon: MoreIcon, href: undefined, id: "more" },
    { label: t("cart"), Icon: BagIcon, href: undefined, id: "cart" },
    { label: t("account"), Icon: UserIcon, href: isAuthenticated ? "/account" : "/account/login", id: "account" },
  ] as const;

  return (
    <>
      <ShopPanel open={shopOpen} onClose={() => { setShopOpen(false); setActive(0); }} t={t} />
      <MorePanel open={moreOpen} onClose={() => { setMoreOpen(false); setActive(0); }} />
      <nav className="fixed bottom-0 left-0 right-0 z-[90] bg-background/95 backdrop-blur-md border-t border-border lg:hidden safe-bottom">
        <div className="grid grid-cols-5 h-14">
          {tabs.map((tab, i) => {
            const isActive = i === active;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActive(i);
                  if (tab.id === "shop") {
                    setMoreOpen(false);
                    setShopOpen(!shopOpen);
                  } else if (tab.id === "more") {
                    setShopOpen(false);
                    setMoreOpen(!moreOpen);
                  } else if (tab.id === "cart") {
                    closeAll();
                    openDrawer();
                  } else {
                    closeAll();
                    if (tab.href) {
                      router.push(tab.href);
                    }
                  }
                }}
                className={`flex flex-col items-center justify-center gap-0.5 transition-colors ${
                  isActive ? "text-foreground" : "text-text-tertiary"
                }`}
                style={{ transitionDuration: "var(--duration-fast)" }}
              >
                <span className="relative">
                  <tab.Icon active={isActive} />
                  {tab.id === "cart" && itemCount > 0 && (
                    <span className="absolute -top-1 -right-2.5 bg-cta text-white text-[8px] font-semibold w-3.5 h-3.5 rounded-full flex items-center justify-center">
                      {itemCount > 99 ? "99+" : itemCount}
                    </span>
                  )}
                </span>
                <span className="text-[10px] font-medium">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
}
